import { ACOES_POR_ID } from './catalogoAcoes'
import { achadoAtualDaVitima } from './efeitosClinicos'
import type {
  DadoClinicoAcao,
  EstadoVitimaNaSessao,
  FichaVitimaAtendimento,
  IdAcaoAvaliacao,
  ProblemaClinicoFicha,
  RegiaoAnatomicaVitima,
  SessaoAtendimentoTabletop,
} from './tipos'

/**
 * O que fica para o debriefing depois do atendimento: o que a equipe deixou
 * de pesquisar, quando percebeu a deterioração e quanto tempo levou para
 * tratar e reavaliar cada problema. Tudo calculado a partir do que já foi
 * registrado — nada aqui aparece durante o atendimento em si.
 */

export interface ItemNuncaPesquisado {
  readonly idAcao: IdAcaoAvaliacao
  readonly rotulo: string
  readonly achado: DadoClinicoAcao
}

/**
 * Achados relevantes da ficha (algo observado ou incerto, não um "sem
 * achado" genérico) que a equipe nunca pediu, no estágio clínico atual.
 */
export function itensNuncaPesquisados(
  ficha: FichaVitimaAtendimento,
  estado: EstadoVitimaNaSessao,
): readonly ItemNuncaPesquisado[] {
  const resultado: ItemNuncaPesquisado[] = []
  for (const idAcao of Object.keys(ficha.achadosIniciais) as IdAcaoAvaliacao[]) {
    if (estado.acoesReveladas.includes(idAcao)) continue
    const achado = achadoAtualDaVitima(ficha, idAcao, estado)
    if (achado.estado === 'observado' || achado.estado === 'incerto') {
      resultado.push({ idAcao, rotulo: ACOES_POR_ID[idAcao]?.rotulo ?? idAcao, achado })
    }
  }
  return resultado
}

/**
 * Instante em que a equipe teve a primeira chance de perceber a
 * deterioração: a primeira leitura (avaliação ou reavaliação) registrada
 * depois que o instrutor avançou o estágio clínico pela primeira vez.
 * `null` sem evolução disparada, ou se nada foi checado depois dela — a
 * deterioração ainda não foi percebida.
 */
export function momentoDeteccaoDeterioracao(
  sessao: SessaoAtendimentoTabletop,
  idVitima: string,
): number | null {
  const eventos = sessao.historicoEventos.filter((e) => e.objetoVitimaId === idVitima)
  const evolucoes = eventos.filter((e) => e.tipo === 'evolucao').map((e) => e.tempoSimuladoSegundos)
  if (evolucoes.length === 0) return null
  const primeiraEvolucao = Math.min(...evolucoes)

  const leiturasDepois = eventos
    .filter((e) => (e.tipo === 'revelacao' || e.tipo === 'reavaliacao') && e.tempoSimuladoSegundos >= primeiraEvolucao)
    .map((e) => e.tempoSimuladoSegundos)
  return leiturasDepois.length > 0 ? Math.min(...leiturasDepois) : null
}

export interface TratamentoDoProblema {
  readonly problema: ProblemaClinicoFicha
  /** instante da primeira intervenção com efeito registrado neste problema, ou `null` se nunca tratado */
  readonly tratadoEm: number | null
  /** instante da primeira reavaliação na região do problema depois de tratado, ou `null` */
  readonly reavaliadoEm: number | null
}

/**
 * Para cada problema da ficha: quando foi tratado (a primeira intervenção
 * que teve efeito registrado nele) e quando a região foi reavaliada depois
 * disso. Mede só o tempo de reação — a qualidade do efeito já aparece em
 * `avaliarIntervencao`.
 */
export function temposDeTratamentoPorProblema(
  ficha: FichaVitimaAtendimento,
  estado: EstadoVitimaNaSessao,
): readonly TratamentoDoProblema[] {
  return (ficha.problemas ?? []).map((problema) => {
    const intervencoes = estado.intervencoesRealizadas
      .filter((i) => i.efeitos?.some((e) => e.idProblema === problema.id))
      .map((i) => i.tempoSimuladoSegundos)
      .sort((a, b) => a - b)
    const tratadoEm = intervencoes[0] ?? null

    let reavaliadoEm: number | null = null
    if (tratadoEm !== null) {
      // a região da leitura em si (guardada no momento da avaliação), não a
      // região "padrão" do exame no catálogo — a ficha pode ser mais específica
      const leiturasDepois = estado.leituras
        .filter((l) => l.achado.regiao === problema.regiao && l.tempoSimuladoSegundos > tratadoEm)
        .map((l) => l.tempoSimuladoSegundos)
        .sort((a, b) => a - b)
      reavaliadoEm = leiturasDepois[0] ?? null
    }

    return { problema, tratadoEm, reavaliadoEm }
  })
}

export interface StatusRegiaoPrevivel {
  readonly regiao: RegiaoAnatomicaVitima
  readonly investigada: boolean
}

/**
 * Compara as regiões que a cinemática da ficha previa (`cinematica.regioesPreviveis`,
 * escrito à mão por quem montou a ficha) com as que a equipe efetivamente
 * investigou (a região de qualquer leitura registrada, em qualquer estágio).
 * Vazio para fichas sem cinemática cadastrada (ex.: pedestre atropelado).
 */
export function regioesPreviveisXInvestigadas(
  ficha: FichaVitimaAtendimento,
  estado: EstadoVitimaNaSessao,
): readonly StatusRegiaoPrevivel[] {
  const regioesInvestigadas = new Set(
    estado.leituras.map((l) => l.achado.regiao).filter((r): r is RegiaoAnatomicaVitima => r !== undefined),
  )
  return (ficha.cinematica?.regioesPreviveis ?? []).map((regiao) => ({
    regiao,
    investigada: regioesInvestigadas.has(regiao),
  }))
}
